import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Solicitudes.
 *
 * Los tipos están escritos a mano y eso es **deuda declarada, no una decisión**:
 * `solicitudes.py` no declara `response_model` en ninguno de sus endpoints, así
 * que `/openapi.json` no describe ninguna respuesta y no hay nada que generar.
 * Están copiados leyendo el router y `page-solicitudes.js`, campo por campo.
 *
 * **Los dieciocho endpoints NO cambian.** Son exactamente los que ya usaba la
 * pantalla vanilla, con los mismos parámetros y los mismos cuerpos: esta
 * migración es de presentación, y el servidor no se entera de nada.
 */

/* ── El tipo de solicitud y su formulario ──────────────────────────────── */

export type TipoDeCampo =
  | 'text' | 'textarea' | 'number' | 'date' | 'rango_fechas'
  | 'email' | 'phone' | 'select' | 'radio' | 'checkbox' | 'file' | 'employee';

export interface CampoDeFormulario {
  key: string;
  label: string;
  type: TipoDeCampo;
  required?: boolean;
  options?: string[];
  help?: string;
  /** Límites de texto y número. Son comodidad: quien valida es el servidor. */
  min_length?: number | null;
  max_length?: number | null;
  min?: number | null;
  max?: number | null;
  pattern?: string | null;
  /** Días de desplazamiento respecto de HOY, no fechas absolutas. */
  date_min?: number | null;
  date_max?: number | null;
  /** Esta fecha no puede ser anterior a la del campo con esta clave. */
  date_after?: string | null;
  /** Duración del período, en días calendario. Solo `rango_fechas`. */
  dias_min?: number | null;
  dias_max?: number | null;
  sin_solape?: boolean;
  /** El servidor lo rellena con la ficha del colaborador y lo bloquea. */
  autofill?: string | null;
  valor_autollenado?: string | null;
  catalog_source?: string | null;
}

export interface PasoDelFlujo {
  label: string;
  approver_type: 'jefe' | 'bienestar' | 'area_manager' | 'final';
}

export interface TipoDeSolicitud {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  icon?: string | null;
  is_active?: boolean;
  fields?: CampoDeFormulario[];
  flow?: PasoDelFlujo[];
  recipient_emails?: string | null;
  report_columns?: { key: string; label: string }[];
  /** Solo en el listado de gestión. */
  field_count?: number;
  step_count?: number;
}

/* ── Una solicitud ─────────────────────────────────────────────────────── */

export type EstadoSolicitud =
  | 'pendiente' | 'en_proceso' | 'aprobada' | 'finalizada'
  | 'rechazada' | 'cancelada' | 'devuelta';

/** Estados en los que ya no hay nada que hacer. Espeja `ESTADOS_CERRADOS` del
 *  backend (`solicitudes.py`). */
export const CERRADAS: EstadoSolicitud[] = ['finalizada', 'rechazada', 'cancelada'];
export const esCerrada = (s?: string) => CERRADAS.includes(s as EstadoSolicitud);

export interface Periodo { inicio: string; fin: string }
export type ValorDeCampo = string | number | string[] | Periodo | null | undefined;

export interface FilaDeSolicitud {
  id: number;
  radicado: string;
  type_name?: string;
  type_icon?: string | null;
  status: EstadoSolicitud;
  created_at: string;
  applicant?: string;
  applicant_id?: number;
  /** Lo calcula el servidor: hay algo que depende del solicitante. */
  accion_requerida?: boolean;
  motivo_accion?: string;
  /** En manos de quién está, ya resuelto a nombre. */
  esperando?: string | null;
}

export interface Solape {
  tipo: string; inicio: string; fin: string; estado: string;
}
export interface AusenciaDeEquipo extends Solape { colaborador: string }

export interface Adjunto {
  key?: string;
  filename: string;
  url: string;
  content_type?: string;
}

export interface Aprobacion {
  step_label: string;
  decision: 'aprobado' | 'rechazado' | 'devuelto';
  note?: string | null;
  approver_name?: string | null;
  created_at?: string | null;
}

export interface Devolucion {
  note?: string | null;
  approver_name?: string | null;
  step_label?: string | null;
  created_at?: string | null;
}

export interface SolicitudDetalle {
  id: number;
  radicado: string;
  type_name?: string;
  type_icon?: string | null;
  status: EstadoSolicitud;
  created_at: string;
  applicant?: string;
  /** Quién la radicó. Lo necesita la pantalla de revisión para no ofrecer una
   *  decisión que el servidor va a rechazar: `approve_request` responde 403 a
   *  quien intenta decidir sobre lo suyo, y eso incluye al admin. */
  applicant_id?: number;
  form_data?: Record<string, ValorDeCampo>;
  fields?: CampoDeFormulario[];
  flow?: PasoDelFlujo[];
  current_step: number;
  current_approver?: { approver_name?: string | null; resolved?: boolean } | null;
  approvals?: Aprobacion[];
  attachments?: Adjunto[];
  soportes_faltantes?: { key: string; label: string }[];
  solapes_propios?: Solape[];
  equipo_ausente?: AusenciaDeEquipo[];
  devolucion?: Devolucion | null;
}

export interface ResumenMias {
  atencion?: number; abiertas?: number; cerradas?: number; todas?: number;
}
export interface PaginaMias {
  items: FilaDeSolicitud[];
  total: number;
  resumen?: ResumenMias;
}

export interface FuenteDeCatalogo { key: string; label: string }
export interface Reporte {
  total: number;
  columns: { key: string; label: string }[];
  rows: Record<string, string | number | null>[];
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

/** Centralizadas: escritas sueltas en cada sitio, una invalidación acaba
 *  fallando por una letra y nadie se entera hasta que un dato guardado no se
 *  refresca. */
export const claves = {
  raiz: ['solicitudes'] as const,
  catalogo: ['solicitudes', 'catalogo'] as const,
  mias: (f: FiltroMias) => ['solicitudes', 'mias', f] as const,
  porAprobar: ['solicitudes', 'por-aprobar'] as const,
  bandeja: (f: FiltroBandeja) => ['solicitudes', 'bandeja', f] as const,
  detalle: (id: number) => ['solicitudes', 'detalle', id] as const,
  tipos: ['solicitudes', 'tipos'] as const,
  tipo: (id: number) => ['solicitudes', 'tipo', id] as const,
  fuentes: ['solicitudes', 'fuentes'] as const,
  reporte: (id: number) => ['solicitudes', 'reporte', id] as const,
};

export interface FiltroMias { estado: string; q: string }
export interface FiltroBandeja { estado: string; q: string }

/* ── Consultas ─────────────────────────────────────────────────────────── */

export function useCatalogo() {
  return useQuery({
    queryKey: claves.catalogo,
    queryFn: () => api.get<TipoDeSolicitud[]>('/solicitudes/catalog'),
  });
}

/** «Por aprobar». Se pide SIEMPRE, también para decidir si la pestaña existe.
 *
 *  La versión vanilla la pedía dos veces seguidas para un colaborador con
 *  pendientes: una en `render()` para saber si enseñaba la pestaña y otra en
 *  `_pending()` para llenarla. Con una sola clave de caché es una petición. */
export function usePorAprobar() {
  return useQuery({
    queryKey: claves.porAprobar,
    queryFn: () => api.get<FilaDeSolicitud[]>('/solicitudes/pending-for-me'),
  });
}

/** Paginación acumulativa: `limit` crece en vez de mover `skip`.
 *
 *  Es lo que permite que «Mostrar más» no invente una lista propia en el
 *  cliente. La versión vanilla concatenaba páginas en `_mineAcum`, una variable
 *  de módulo que sobrevivía a la navegación: volver a la pestaña enseñaba la
 *  lista vieja hasta que respondiera la nueva. */
export function useMias(filtro: FiltroMias, limite: number) {
  return useQuery({
    queryKey: [...claves.mias(filtro), limite],
    queryFn: () => {
      const p = new URLSearchParams({ skip: '0', limit: String(limite) });
      if (filtro.estado !== 'todas') p.set('estado', filtro.estado);
      if (filtro.q) p.set('q', filtro.q);
      return api.get<PaginaMias>(`/solicitudes/mine?${p}`);
    },
    placeholderData: (anterior) => anterior,
  });
}

export function useBandeja(filtro: FiltroBandeja, limite: number) {
  return useQuery({
    queryKey: [...claves.bandeja(filtro), limite],
    queryFn: () => {
      const p = new URLSearchParams({ skip: '0', limit: String(limite) });
      if (filtro.estado !== 'todas') p.set('status', filtro.estado);
      if (filtro.q) p.set('q', filtro.q);
      return api.get<{ items: FilaDeSolicitud[]; total: number }>(`/solicitudes?${p}`);
    },
    placeholderData: (anterior) => anterior,
  });
}

export function useDetalle(id: number | null) {
  return useQuery({
    queryKey: claves.detalle(id ?? 0),
    queryFn: () => api.get<SolicitudDetalle>(`/solicitudes/${id}`),
    enabled: id != null,
  });
}

export function useTipos() {
  return useQuery({
    queryKey: claves.tipos,
    queryFn: () => api.get<TipoDeSolicitud[]>('/solicitudes/types'),
  });
}

export function useTipo(id: number | null) {
  return useQuery({
    queryKey: claves.tipo(id ?? 0),
    queryFn: () => api.get<TipoDeSolicitud>(`/solicitudes/types/${id}`),
    enabled: id != null,
  });
}

export function useFuentes(habilitado: boolean) {
  return useQuery({
    queryKey: claves.fuentes,
    queryFn: () => api.get<{ sources?: FuenteDeCatalogo[]; autofill?: FuenteDeCatalogo[] }>(
      '/solicitudes/catalog-sources'),
    enabled: habilitado,
    // Los catálogos del sistema no cambian mientras alguien configura un tipo.
    staleTime: 5 * 60_000,
  });
}

export function useReporte(id: number | null) {
  return useQuery({
    queryKey: claves.reporte(id ?? 0),
    queryFn: () => api.get<Reporte>(`/solicitudes/types/${id}/report`),
    enabled: id != null,
  });
}

/* ── Mutaciones ────────────────────────────────────────────────────────── */

/** Sube los soportes de una solicitud YA radicada. Devuelve las ETIQUETAS de
 *  los que no se pudieron subir.
 *
 *  Radicar son dos viajes: primero se crea la solicitud, después se suben los
 *  archivos. El segundo iba envuelto en un `catch` vacío en los dos sitios que
 *  lo hacen, así que si MinIO no respondía —o el archivo pasaba de 15 MB— el
 *  colaborador veía el toast de éxito con su radicado y se iba. El soporte
 *  obligatorio no estaba, el aprobador se topaba con el bloqueo días después, y
 *  a nadie se le avisó en el único momento en que aún tenía el archivo a mano.
 *
 *  El error NO se relanza: el radicado es válido y la solicitud existe. Esto no
 *  es un fallo de envío, es un aviso — y por eso quien llama lo redacta así. */
export async function subirSoportes(
  id: number,
  archivos: { key: string; file: File }[],
  campos?: CampoDeFormulario[],
): Promise<string[]> {
  const etiqueta: Record<string, string> = {};
  (campos ?? []).forEach((f) => { etiqueta[f.key] = f.label || f.key; });
  const fallidos: string[] = [];
  for (const a of archivos) {
    const fd = new FormData();
    fd.append('file', a.file);
    try {
      await api.upload(
        `/solicitudes/${id}/attachments?field_key=${encodeURIComponent(a.key)}`, fd);
    } catch {
      fallidos.push(etiqueta[a.key] || a.key);
    }
  }
  return fallidos;
}

export function useRadicar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { request_type_id: number; form_data: Record<string, ValorDeCampo> }) =>
      api.post<{ id: number; radicado: string; aviso?: string }>('/solicitudes', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useReenviar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, form_data }: { id: number; form_data: Record<string, ValorDeCampo> }) =>
      api.post(`/solicitudes/${id}/resubmit`, { form_data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useCancelar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      api.post(`/solicitudes/${id}/cancel`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useDecidir() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }:
      { id: number; decision: 'aprobado' | 'rechazado' | 'devuelto'; note: string }) =>
      api.post<{ status?: string }>(`/solicitudes/${id}/approve`, { decision, note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useAdjuntar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fieldKey, file }: { id: number; fieldKey: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload(
        `/solicitudes/${id}/attachments?field_key=${encodeURIComponent(fieldKey)}`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useGuardarTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: Partial<TipoDeSolicitud> }) =>
      id ? api.put(`/solicitudes/types/${id}`, body) : api.post('/solicitudes/types', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

export function useBorrarTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message?: string }>(`/solicitudes/types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.raiz }),
  });
}

/* ── Reglas de fecha. Las mismas que aplica el servidor ────────────────── */

/** Días calendario entre dos fechas, contando ambos extremos. Debe dar el mismo
 *  número que `duracion_dias()` del backend. */
export function diasDeRango(inicio?: string, fin?: string): number | null {
  if (!inicio || !fin) return null;
  const a = new Date(`${inicio}T00:00:00`);
  const b = new Date(`${fin}T00:00:00`);
  if (Number.isNaN(+a) || Number.isNaN(+b) || b < a) return null;
  return Math.round((+b - +a) / 86_400_000) + 1;
}

/** «4 de agosto de 2026». Se construye a medianoche LOCAL: `new Date('2026-08-04')`
 *  se lee como UTC y en Colombia retrocede un día. */
export function fechaLarga(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const plDias = (n: number) => `${n} día${n === 1 ? '' : 's'}`;

/** Una fecha `yyyy-mm-dd` a N días de hoy. Los límites del servidor son
 *  desplazamientos, no fechas absolutas. */
export function fechaDesplazada(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  // `toISOString()` pasa por UTC: en Colombia, a partir de las 19:00 devolvería
  // el día siguiente y el calendario ofrecería un día que el servidor rechaza.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function reglaDeDuracion(f: CampoDeFormulario): string {
  const min = f.dias_min ? Number(f.dias_min) : null;
  const max = f.dias_max ? Number(f.dias_max) : null;
  if (min && max && min === max) return `Este período debe durar exactamente ${plDias(min)} calendario.`;
  if (min && max) return `Duración permitida: entre ${min} y ${max} días calendario.`;
  if (min) return `Duración mínima permitida: ${plDias(min)} calendario.`;
  if (max) return `Duración máxima permitida: ${plDias(max)} calendario.`;
  return '';
}

export type EstadoRango = { estado: 'neutral' | 'ok' | 'error'; titulo: string; detalle?: string };

/** Qué decirle al usuario según lo que lleve elegido. Devuelve SIEMPRE un
 *  mensaje: el campo nunca está mudo, ni siquiera vacío. `detalle` repite el
 *  período en palabras porque «5 días» a secas no permite confirmar CUÁLES. */
export function estadoDeRango(f: CampoDeFormulario, a?: string, b?: string): EstadoRango {
  if (!a && !b) return { estado: 'neutral', titulo: 'Selecciona ambas fechas para calcular la duración del período.' };
  if (a && !b) return { estado: 'neutral', titulo: 'Ahora selecciona la fecha de finalización.' };
  if (!a && b) return { estado: 'neutral', titulo: 'Ahora selecciona la fecha de inicio.' };
  if (b! < a!) return { estado: 'error', titulo: 'La fecha de finalización no puede ser anterior a la fecha de inicio.' };
  const periodo = `Del ${fechaLarga(a!)} al ${fechaLarga(b!)}`;
  const d = diasDeRango(a, b)!;
  const min = f.dias_min ? Number(f.dias_min) : null;
  const max = f.dias_max ? Number(f.dias_max) : null;
  if (min && d < min) {
    return { estado: 'error', detalle: periodo,
      titulo: `El período seleccionado tiene ${plDias(d)}. La duración mínima permitida es de ${plDias(min)}.` };
  }
  if (max && d > max) {
    return { estado: 'error', detalle: periodo,
      titulo: `El período seleccionado tiene ${plDias(d)}. La duración máxima permitida es de ${plDias(max)}.` };
  }
  return { estado: 'ok', titulo: `Solicitarás ${plDias(d)} calendario`, detalle: periodo };
}

/** Cuántos días lleva radicada una solicitud, y si eso ya es demasiado.
 *
 *  Se mide desde `created_at` y no desde que llegó a este paso, a propósito: lo
 *  que importa es cuánto lleva esperando LA PERSONA. Que el retraso se haya
 *  acumulado en un paso anterior no se lo devuelve.
 *
 *  El umbral no sale de ningún acuerdo de servicio —no hay ninguno escrito en
 *  el sistema—, así que es un criterio y se deja dicho: hasta dos días es
 *  normal, a partir de tres se avisa y pasada una semana laboral se marca. */
export function esperaDesde(createdAt?: string) {
  if (!createdAt) return null;
  const dias = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  if (dias < 0 || !Number.isFinite(dias)) return null;
  const nivel: 'normal' | 'alerta' | 'malo' = dias > 5 ? 'malo' : dias >= 3 ? 'alerta' : 'normal';
  return { dias, nivel, etiqueta: dias === 0 ? 'Hoy' : plDias(dias) };
}

export const ESTADO_TXT: Record<string, string> = {
  pendiente: 'Pendiente', en_proceso: 'En proceso', aprobada: 'Aprobada',
  finalizada: 'Finalizada', rechazada: 'Rechazada',
  // Gris a propósito: cancelada no es una negativa de la empresa, es un retiro
  // del propio solicitante. En rojo se leería como un rechazo.
  cancelada: 'Cancelada',
  // Ámbar: no es un cierre, es una tarea pendiente del colaborador.
  devuelta: 'Devuelta',
};

export const ESTADO_TONO: Record<string, 'neutral' | 'info' | 'ok' | 'warn' | 'danger'> = {
  pendiente: 'info', en_proceso: 'info', aprobada: 'ok', finalizada: 'ok',
  rechazada: 'danger', cancelada: 'neutral', devuelta: 'warn',
};
